import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import format from 'date-fns/format'
import * as Modal from '_components/modal/generic-modal'
import Button from '_components/button'
import { WhatsApp } from '@material-ui/icons'

import { getTradesmanSequenceByID } from '_/modules/tradesman-sequence/selectors'
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'

import useStyles from './styles'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import { openWhatsAppWithMessage } from '_/utils/open-whatsApp'

const formatDateLabel = dateStr => {
  const date = new Date(dateStr)
  return dateStr ? format(date, "dd/MM/yyyy ', às ' HH:mm'h'") : null
}

const SequenceModal = ({ isOpen, onClose, sequenceId, name = 'Sequência' }) => {
  const styles = useStyles()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()

  const sequence = useSelector(getTradesmanSequenceByID(sequenceId))

  const modalDescriptionLabel = useMemo(() => {
    if (sequence) {
      const creationDate = formatDateLabel(sequence.createdAt)
      const responsibleName = sequence.createdBy
      return `Sequência criada em ${creationDate}, por ${responsibleName}`
    }
    return ''
  }, [sequence])

  const handleWhatsAppButtonClick = phone => {
    if (!phone) {
      showToast({
        message: 'Este prestador não tem contato telefônico em seu cadastro.',
        type: 'error',
      })
      return
    }

    if (!sequence?.isActive) {
      showDialog({
        subject: 'Atenção',
        description:
          'Esta não é a sequência ativa no momento. Tem certeza que deseja contatar este prestador?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          openWhatsAppWithMessage({ phone })
          closeDialog()
        },
        onCancel: closeDialog,
        type: 'warning',
      })
      return
    }

    openWhatsAppWithMessage({ phone })
  }

  const renderTableBody = useMemo(() => {
    if (sequence) {
      const { candidates } = sequence

      return candidates?.map(cell => {
        const currentKey = crypto.randomUUID()
        const {
          serviceProvider: { companyName, phone },
          round,
          appointedAt,
          unappointedAt,
          unappointmentReason,
          isAppointed,
        } = cell

        const reason = isAppointed ? 'Atrelado' : unappointmentReason?.reason || 'Não atrelado'

        return (
          <TableRow key={`candidate-row-${currentKey}`}>
            <TableCell key={`companyName-${currentKey}`}>
              <Typography>{companyName}</Typography>
            </TableCell>
            <TableCell key={`round-${currentKey}`}>
              <Typography>{isNaN(parseInt(round, 10)) ? '-' : round}</Typography>
            </TableCell>
            <TableCell key={`appointed-at-${currentKey}`}>
              <Typography>{formatDateLabel(appointedAt) || '-'}</Typography>
            </TableCell>
            <TableCell key={`unappointed-at-${currentKey}`}>
              <Typography>{formatDateLabel(unappointedAt) || '-'}</Typography>
            </TableCell>
            <TableCell key={`reason-${currentKey}`}>
              <Grid container>
                <Typography className={styles.chipReasons}>{reason}</Typography>
              </Grid>
            </TableCell>
            <TableCell key={`whatsapp-${currentKey}`}>
              <Button
                variant="default"
                title="Enviar mensagem de whatsapp"
                onClick={() => handleWhatsAppButtonClick(phone)}
              >
                <WhatsApp className={styles.iconWhatsApp} />
              </Button>
            </TableCell>
          </TableRow>
        )
      })
    }

    return null
  }, [sequence])

  return (
    <Modal.Root open={isOpen} title={name} onClose={onClose} className={styles.root}>
      <Modal.TitleModal title={name} />
      <Modal.Content className={styles.modalConteiner}>
        <Typography>{modalDescriptionLabel}</Typography>
        <Table sx={{ marginTop: '24px' }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography className={styles.columnHeader}>Prestador</Typography>
              </TableCell>
              <TableCell>
                <Typography className={styles.columnHeader}>Rodada</Typography>
              </TableCell>
              <TableCell>
                <Typography className={styles.columnHeader}>Atrelado em</Typography>
              </TableCell>
              <TableCell>
                <Typography className={styles.columnHeader}>Desatrelado em</Typography>
              </TableCell>
              <TableCell>
                <Typography className={styles.columnHeader}>Status</Typography>
              </TableCell>
              <TableCell>
                <Typography className={styles.columnHeader} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody}</TableBody>
        </Table>
      </Modal.Content>
      <Modal.Actions className={styles.buttonsContainer}>
        <Modal.ButtonBlue onClick={onClose}>Fechar</Modal.ButtonBlue>
      </Modal.Actions>
    </Modal.Root>
  )
}

export default SequenceModal
